// frontend/src/services/login.js
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://138.201.254.35:8000';

// Create a separate instance for login to avoid interceptors interfering
const loginApi = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  timeout: 10000,
});

// Main API instance with interceptors
const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  timeout: 10000,
});

// Request interceptor for main API
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for main API
api.interceptors.response.use(
  (response) => {
    console.log('API Response:', response.status, response.data);
    return response;
  },
  (error) => {
    console.error('API Error:', error);
    if (error.response?.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authService = {
  async login(email, password, rememberMe = false) {
    try {
      console.log('Login attempt:', email);
      
      const formData = new URLSearchParams();
      formData.append('username', email);
      formData.append('password', password);
      
      // Use the loginApi instance without interceptors for login
      const response = await loginApi.post('/auth/login', formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });
      
      console.log('Login response received:', response.status);
      console.log('Response data:', response.data);
      
      if (response.data && response.data.access_token) {
        localStorage.setItem('authToken', response.data.access_token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        
        if (rememberMe) {
          localStorage.setItem('rememberMe', 'true');
        }
        
        return response.data;
      } else {
        console.error('Invalid response format:', response.data);
        throw new Error('Invalid response format from server');
      }
    } catch (error) {
      console.error('Login service error details:', error);
      
      // Check for CORS issues
      if (error.message && error.message.includes('Network Error')) {
        console.error('CORS or network issue detected');
        throw new Error('Cannot connect to server. Please check if the server is running and CORS is configured.');
      }
      
      // Handle different error types
      if (error.code === 'ECONNABORTED') {
        throw new Error('Request timeout. Please try again.');
      } else if (error.response) {
        // Server responded with error status
        if (error.response.status === 401) {
          throw new Error('Invalid email or password');
        } else if (error.response.status >= 500) {
          throw new Error('Server error. Please try again later.');
        } else {
          throw new Error(error.response.data?.detail || `Login failed (${error.response.status})`);
        }
      } else if (error.request) {
        // Request was made but no response received
        console.error('No response received:', error.request);
        throw new Error('Network error. Please check your connection and try again.');
      } else {
        // Something else happened
        throw new Error(error.message || 'An unexpected error occurred');
      }
    }
  },

  async logout() {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      localStorage.removeItem('rememberMe');
    }
  },

  async getCurrentUser() {
    try {
      const response = await api.get('/auth/me');
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) {
        this.logout();
      }
      throw error;
    }
  },

  isAuthenticated() {
    const token = localStorage.getItem('authToken');
    const user = localStorage.getItem('user');
    return !!(token && user);
  },

  getAuthHeader() {
    const token = localStorage.getItem('authToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  },
};

export default api;